import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createThemePatterns, createColorPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['led', 'flat', 'glow', 'minimal', 'traffic-light'] as const;
type LightTheme = typeof VALID_THEMES[number];

const VALID_STATUSES = ['ok', 'warning', 'critical', 'offline', 'custom'] as const;
const VALID_BLINK_MODES = ['none', 'slow', 'fast', 'pulse'] as const;
const VALID_SHAPES = ['circle', 'square', 'diamond'] as const;

const THEME_ALIASES: Record<string, string> = {
  'basic': 'flat',
  'simple': 'minimal',
  'clean': 'minimal',
  'neon': 'glow',
  'traffic': 'traffic-light',
};

const STATUS_ALIASES: Record<string, string> = {
  'green': 'ok', 'good': 'ok', 'active': 'ok', 'on': 'ok', 'online': 'ok', 'healthy': 'ok',
  'yellow': 'warning', 'amber': 'warning', 'caution': 'warning', 'warn': 'warning',
  'red': 'critical', 'error': 'critical', 'alert': 'critical', 'danger': 'critical', 'alarm': 'critical',
  'gray': 'offline', 'grey': 'offline', 'off': 'offline', 'inactive': 'offline', 'disabled': 'offline',
};

export class StatusLightCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Theme patterns via mixin
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }

    // Status color patterns: "set ok color to green", "set warning color to yellow"
    for (const [regex, handler] of createColorPatterns(['okColor', 'warningColor', 'criticalColor', 'offlineColor'])) {
      this.commandPatterns.set(regex, handler);
    }

    // ─── Threshold commands ─────────────────────────────────────────────
    this.commandPatterns.set(
      /(?:set )?warning threshold (?:to |at |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { warningThreshold: parseFloat(match[1]) } })
    );

    this.commandPatterns.set(
      /(?:set )?critical threshold (?:to |at |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { criticalThreshold: parseFloat(match[1]) } })
    );

    this.commandPatterns.set(
      /threshold direction (?:to )?(above|below|higher|lower)/i,
      (match) => {
        const dir = match[1].toLowerCase();
        return { properties: { thresholdDirection: (dir === 'below' || dir === 'lower') ? 'below' : 'above' } };
      }
    );

    this.commandPatterns.set(
      /clear thresholds?/i,
      () => ({ properties: { warningThreshold: null, criticalThreshold: null, thresholds: null } })
    );

    // ─── Status (static fallback) ───────────────────────────────────────
    this.commandPatterns.set(
      /(?:set )?status (?:to )?([\w-]+)/i,
      (match) => {
        const status = this.resolveStatus(match[1].trim());
        return status ? { properties: { status } } : null;
      }
    );

    this.commandPatterns.set(
      /(?:make (?:it )?|show (?:as )?)(ok|warning|critical|offline|green|red|yellow|amber|gray|grey|active|inactive|online|healthy|error|alert|danger)/i,
      (match) => {
        const status = this.resolveStatus(match[1].trim());
        return status ? { properties: { status } } : null;
      }
    );

    // ─── Blink mode ─────────────────────────────────────────────────────
    this.commandPatterns.set(
      /(?:set )?blink(?:ing)? (?:to |mode )?(none|slow|fast|pulse)/i,
      (match) => {
        const mode = match[1].toLowerCase();
        return (VALID_BLINK_MODES as readonly string[]).includes(mode) ? { properties: { blinkMode: mode } } : null;
      }
    );

    // Conditional blink: "blink on critical", "blink when warning"
    this.commandPatterns.set(
      /blink (?:on |when |if )(critical|warning|offline)/i,
      (match) => ({ properties: { blinkOnStatus: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /\b(pulse|blink)\b/i,
      (match) => {
        if (match[1].toLowerCase() === 'pulse') return { properties: { blinkMode: 'pulse' } };
        return { properties: { blinkMode: 'slow' } };
      }
    );

    this.commandPatterns.set(
      /stop (?:blink(?:ing)?|puls(?:e|ing))/i,
      () => ({ properties: { blinkMode: 'none', blinkOnStatus: null } })
    );

    // ─── Shape ──────────────────────────────────────────────────────────
    this.commandPatterns.set(
      /(?:set )?shape (?:to )?(circle|square|diamond)/i,
      (match) => ({ properties: { shape: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /(?:make (?:it )?)(circle|square|diamond)/i,
      (match) => {
        const s = match[1].toLowerCase();
        return (VALID_SHAPES as readonly string[]).includes(s) ? { properties: { shape: s } } : null;
      }
    );

    // ─── Size ───────────────────────────────────────────────────────────
    this.commandPatterns.set(
      /(?:set )?size (?:to )?(sm|small|md|medium|lg|large)/i,
      (match) => {
        const sizeMap: Record<string, string> = {
          sm: 'sm', small: 'sm', md: 'md', medium: 'md', lg: 'lg', large: 'lg',
        };
        return { properties: { size: sizeMap[match[1].toLowerCase()] || 'md' } };
      }
    );

    // ─── Label ──────────────────────────────────────────────────────────
    this.commandPatterns.set(
      /(?:set )?label (?:to )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { label: match[1].trim() } })
    );

    this.commandPatterns.set(
      /(?:show|display) label/i,
      () => ({ properties: { showLabel: true } })
    );

    this.commandPatterns.set(
      /(?:hide|remove) label/i,
      () => ({ properties: { showLabel: false } })
    );
  }

  private resolveStatus(input: string): string | null {
    const lower = input.toLowerCase();
    if ((VALID_STATUSES as readonly string[]).includes(lower)) return lower;
    if (STATUS_ALIASES[lower]) return STATUS_ALIASES[lower];
    return null;
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.STATUS_LIGHT && this.hasLightKeywords(command);
  }

  private hasLightKeywords(command: string): boolean {
    const keywords = [
      "status", "light", "indicator", "led", "blink", "pulse",
      "theme", "color", "shape", "size", "label",
      "ok", "warning", "critical", "offline",
      "green", "red", "yellow", "amber", "gray",
      "circle", "square", "diamond",
      "show", "hide", "threshold", "clear", "direction",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: "Could not understand status light command",
        suggestions: await this.getExampleCommands(ComponentType.STATUS_LIGHT),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      "Set warning threshold to 70",
      "Set critical threshold to 90",
      "Threshold direction below",
      "Clear thresholds",
      "Set ok color to green",
      "Blink on critical",
      "Theme glow",
      "Shape diamond",
      "Size large",
      "Label Pump Status",
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = (component.properties || {}) as Record<string, unknown>;
    const currentBlink = (props.blinkMode as string) ?? 'none';
    const hasThresholds = props.warningThreshold != null || props.criticalThreshold != null;

    if (!hasThresholds) {
      suggestions.push("Set warning threshold to 70");
      suggestions.push("Set critical threshold to 90");
    } else {
      suggestions.push("Clear thresholds");
    }

    if (currentBlink === 'none' && !props.blinkOnStatus) {
      suggestions.push("Blink on critical");
    } else {
      suggestions.push("Stop blinking");
    }

    suggestions.push("Theme glow");
    suggestions.push("Shape diamond");

    return suggestions.slice(0, 6);
  }
}

export const statusLightCommandHandler = new StatusLightCommandHandler();
